import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-expo";
import { Link } from "expo-router";
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from "react-native";

import { fetchShelfEntry, setShelfStatus, type ShelfStatus } from "./api";
import { colour, type } from "./theme";

/**
 * Own / Wishlist for one game (WI-031).
 *
 * The first control in the app that writes anything. Two things it takes care
 * over: it does not pretend to work when signed out, and a failed write puts
 * the button back rather than leaving the screen claiming something that did
 * not happen.
 */

const OPTIONS: { value: ShelfStatus; label: string }[] = [
  { value: "owned", label: "Own it" },
  { value: "wishlist", label: "Want it" },
];

export function ShelfControl({ slug, version = 0 }: { slug: string; version?: number }) {
  const { isLoaded, isSignedIn } = useAuth();
  const [status, setStatus] = useState<ShelfStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setStatus(null);
      return;
    }
    const controller = new AbortController();
    fetchShelfEntry(slug, controller.signal)
      .then((entry) => {
        if (!controller.signal.aborted) setStatus(entry.status);
      })
      .catch(() => {
        // A shelf that will not load is not worth an error banner on a page
        // whose job is showing a game. The buttons stay usable.
        if (!controller.signal.aborted) setStatus("none");
      });
    return () => controller.abort();
    // `version` changes when a play is logged. The rating shown here is
    // projected from sessions (ADR-0006), so it moves without this
    // component doing anything.
  }, [slug, isLoaded, isSignedIn, version]);

  const choose = useCallback(
    (value: ShelfStatus) => {
      if (busy) return;
      const next = status === value ? "none" : value;
      const previous = status;

      // Optimistic, because a shelf toggle that waits on a round trip feels
      // broken -- but remembered, so a failure can put it back rather than
      // leaving the screen asserting something the server never accepted.
      setStatus(next);
      setBusy(true);
      setError(null);

      setShelfStatus(slug, next)
        .then((entry) => setStatus(entry.status))
        .catch((cause: Error) => {
          setStatus(previous);
          setError(cause.message);
        })
        .finally(() => setBusy(false));
    },
    [slug, status, busy],
  );

  if (!isLoaded) return null;

  if (!isSignedIn) {
    return (
      <View style={styles.row}>
        <Link href="/sign-in" asChild>
          <Pressable style={styles.signIn}>
            <Text style={styles.signInText}>Sign in to add this to your shelf</Text>
          </Pressable>
        </Link>
      </View>
    );
  }

  return (
    <View style={styles.wrap}>
      <View style={styles.row}>
        {OPTIONS.map((option) => {
          const active = status === option.value;
          return (
            <Pressable
              key={option.value}
              onPress={() => choose(option.value)}
              disabled={busy}
              style={[styles.button, active && styles.buttonActive, busy && styles.buttonBusy]}
            >
              <Text style={[styles.label, active && styles.labelActive]}>
                {active ? `✓ ${option.label}` : option.label}
              </Text>
            </Pressable>
          );
        })}
        {busy && <ActivityIndicator size="small" color={colour.inkMuted} />}
      </View>
      {error !== null && <Text style={styles.error}>{error}</Text>}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: 6 },
  row: { flexDirection: "row", gap: 8, alignItems: "center", flexWrap: "wrap" },
  button: {
    borderWidth: 1,
    borderColor: colour.lineStrong,
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 14,
    backgroundColor: colour.surface,
  },
  buttonActive: { backgroundColor: colour.ink, borderColor: colour.ink },
  buttonBusy: { opacity: 0.6 },
  label: { ...type.meta, color: colour.inkMuted, fontWeight: "600" },
  labelActive: { color: colour.surface },
  signIn: {
    borderWidth: 1,
    borderColor: colour.lineStrong,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: colour.surface,
  },
  signInText: { ...type.meta, color: colour.accent, fontWeight: "600" },
  error: { color: colour.danger, ...type.meta },
});
